import React, { useEffect, useState, useCallback } from "react";
import { MagnifyingGlass, CaretLeft, CaretRight, Key, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";

const STATUS = {
  active: { label: "Aktiv", color: "#22C55E" },
  premium: { label: "Premium", color: "#F59E0B" },
  guest: { label: "Gast", color: "#0EA5E9" },
  converted: { label: "Konvertiert", color: "#A855F7" },
  admin: { label: "Admin", color: "#EF4444" },
};
const FILTERS = [["all", "Alle"], ["active", "Aktiv"], ["premium", "Premium"], ["guest", "Gast"], ["converted", "Konvertiert"], ["admin", "Admin"]];
const PAGE_SIZE = 20;

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || "—"; }
};

export default function AdminRegistrations() {
  const [data, setData] = useState({ total: 0, page: 1, page_size: PAGE_SIZE, users: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [assign, setAssign] = useState(null); // { user }
  const [count, setCount] = useState("1");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/users", { params: { page, page_size: PAGE_SIZE, q, status } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [page, q, status]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);
  useEffect(() => { setPage(1); }, [q, status]);

  const openAssign = (u) => { setAssign({ user: u }); setCount("1"); setNote(""); };

  const submitAssign = async () => {
    const n = parseInt(count, 10);
    if (!n || n < 1) { toast.error("Bitte mindestens 1 Aktivierung angeben"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${assign.user.user_id}/activations`, { count: n, note });
      toast.success(`${n} Aktivierung(en) zugewiesen · neuer Stand: ${r.data.new_balance}`);
      setAssign(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="sb-card p-5" data-testid="admin-registrations">
      <p className="font-body text-sm text-gray-500 mb-3">Alle registrierten Nutzer · <b>{data.total}</b> gesamt</p>

      <div className="relative mb-2">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input data-testid="reg-search" className="sb-input pl-9" placeholder="Name oder E-Mail suchen" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="reg-filters">
        {FILTERS.map(([v, l]) => (
          <button key={v} data-testid={`reg-filter-${v}`} onClick={() => setStatus(v)}
            className={`px-2.5 py-1 rounded-full text-xs font-heading border-2 ${status === v ? "text-white border-transparent" : "bg-[var(--app-surface)] text-gray-500 border-[var(--app-border)]"}`}
            style={status === v ? { background: "var(--app-primary)" } : {}}>{l}</button>
        ))}
      </div>

      {loading && <p className="font-body text-sm text-gray-500">…</p>}
      {!loading && data.users.length === 0 && <p className="font-body text-sm text-gray-500" data-testid="reg-empty">Keine Nutzer gefunden.</p>}

      <div className="space-y-2" data-testid="reg-list">
        {data.users.map((u, i) => {
          const s = STATUS[u.status] || STATUS.active;
          return (
            <div key={u.user_id} data-testid={`reg-row-${i}`} className="flex items-center justify-between gap-2 border-2 border-[var(--app-border)] rounded-2xl px-3 py-2.5 bg-[var(--app-surface)]">
              <div className="min-w-0">
                <p className="font-heading text-sm truncate">{u.name}</p>
                <p className="font-body text-xs text-gray-500 truncate">{u.email || "—"}</p>
                <p className="font-body text-[11px] text-gray-400 mt-0.5">{fmt(u.created_at)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="flex items-center gap-1 text-[11px] font-heading text-gray-600" title="Aktivierungsguthaben" data-testid={`reg-credits-${i}`}>
                  <Key size={13} weight="duotone" style={{ color: "var(--app-strong)" }} />{u.activation_credits ?? 0}
                </span>
                <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white" style={{ background: s.color }} data-testid={`reg-status-${i}`}>{s.label}</span>
                <button data-testid={`reg-assign-${i}`} onClick={() => openAssign(u)} title="Aktivierungen zuweisen"
                  className="w-8 h-8 rounded-xl border-2 border-[var(--app-border)] bg-[var(--app-bg)] flex items-center justify-center active:scale-90 transition-transform" style={{ color: "var(--app-strong)" }}>
                  <Plus size={16} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4" data-testid="reg-pagination">
          <button data-testid="reg-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="sb-btn px-3 py-2 bg-[var(--app-surface)] text-sm flex items-center gap-1 disabled:opacity-40"><CaretLeft size={14} weight="bold" />Zurück</button>
          <span className="font-body text-xs text-gray-500">Seite {data.page} von {totalPages}</span>
          <button data-testid="reg-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="sb-btn px-3 py-2 bg-[var(--app-surface)] text-sm flex items-center gap-1 disabled:opacity-40">Weiter<CaretRight size={14} weight="bold" /></button>
        </div>
      )}

      <Dialog open={!!assign} onOpenChange={(o) => !o && setAssign(null)}>
        <DialogContent data-testid="assign-activations-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Key size={20} weight="duotone" style={{ color: "var(--app-strong)" }} />Aktivierungen zuweisen</DialogTitle>
            <DialogDescription>
              {assign ? `${assign.user.name} · ${assign.user.email || "—"} · aktuell ${assign.user.activation_credits ?? 0}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="font-body text-xs text-gray-500">Anzahl Aktivierungen</label>
              <input data-testid="assign-count" type="number" min="1" className="sb-input mt-1" value={count} onChange={(e) => setCount(e.target.value)} />
            </div>
            <div>
              <label className="font-body text-xs text-gray-500">Interne Notiz (optional)</label>
              <textarea data-testid="assign-note" className="sb-input mt-1" rows={2} placeholder="Nur intern sichtbar" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <button data-testid="assign-submit" onClick={submitAssign} disabled={busy}
              className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>
              {busy ? "…" : "Aktivierungen zuweisen"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
